import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BrandLogo from '../../components/brand/BrandLogo';
import { Loader2, ArrowRight, Check, X } from 'lucide-react';

const REQUIREMENTS = [
  { key: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { key: 'uppercase', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'digit', label: 'One number', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'One special character', test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(p) },
];

export default function Register() {
  const shellRef = useRef(null);
  const contentRef = useRef(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [responsiveLayout, setResponsiveLayout] = useState({ scale: 1, height: null });
  const { register } = useAuth();
  const navigate = useNavigate();

  const checks = useMemo(
    () => REQUIREMENTS.map((rule) => ({ ...rule, passed: rule.test(password) })),
    [password],
  );
  const allPassed = checks.every((check) => check.passed);

  useEffect(() => {
    const updateResponsiveLayout = () => {
      if (!shellRef.current || !contentRef.current) return;

      const availableHeight = Math.max(shellRef.current.clientHeight - 32, 0);
      const contentHeight = contentRef.current.scrollHeight;
      const rawScale = contentHeight > availableHeight ? availableHeight / contentHeight : 1;
      const scale = Math.max(0.72, Math.min(1, rawScale));
      const nextLayout = {
        scale: Number(scale.toFixed(3)),
        height: Math.round(contentHeight * scale),
      };

      setResponsiveLayout((current) => (
        current.scale === nextLayout.scale && current.height === nextLayout.height
          ? current
          : nextLayout
      ));
    };

    const frame = window.requestAnimationFrame(updateResponsiveLayout);
    const resizeObserver = new ResizeObserver(updateResponsiveLayout);
    resizeObserver.observe(shellRef.current);
    resizeObserver.observe(contentRef.current);
    document.fonts?.ready.then(updateResponsiveLayout);
    window.addEventListener('resize', updateResponsiveLayout);
    window.visualViewport?.addEventListener('resize', updateResponsiveLayout);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateResponsiveLayout);
      window.visualViewport?.removeEventListener('resize', updateResponsiveLayout);
    };
  }, [error]);

  const isScaled = responsiveLayout.scale < 0.999;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!allPassed) {
      setError('Please satisfy all password requirements.');
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      navigate('/app');
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-[#F7F8F6] lg:flex-row">
      <div className="hidden lg:flex lg:min-h-0 flex-1 bg-[#0B1D3A] text-white items-center justify-center px-12 py-14">
        <div className="max-w-lg">
          <BrandLogo variant="horizontal" tone="dark" size="lg" loading="eager" className="mb-8" />
          <h2 className="text-4xl font-display leading-tight">
            One workspace for outreach, approvals, and client-facing follow-up.
          </h2>
          <p className="mt-5 text-[15px] leading-7 text-white/72">
            Linirio keeps the operator loop tight. Inbound messages, AI suggestions, and next-step tasks stay in one workflow.
          </p>
          <div className="mt-10 space-y-3">
            {[
              'Real inbox sync and operator review',
              'Configurable AI reply approvals',
              'Client task queue for quotes and meetings',
              'Reports and audit in the same product shell',
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#7BE36A] text-[#0B1D3A] flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
                <span className="text-sm text-white/84">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div ref={shellRef} className="flex h-full min-h-0 flex-1 items-center justify-center overflow-hidden px-4 py-4 sm:px-8 lg:px-12">
        <div
          className="relative w-full max-w-md"
          style={isScaled ? { height: `${responsiveLayout.height}px` } : undefined}
        >
          <div
            ref={contentRef}
            className="mx-auto w-full max-w-md"
            style={isScaled ? {
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: `translateX(-50%) scale(${responsiveLayout.scale})`,
              transformOrigin: 'top center',
            } : undefined}
          >
            <div className="brand-card p-6 sm:p-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6B7280]">Workspace access</p>
            <h1 className="mt-3 text-3xl font-display text-[#0B1D3A]">Create account</h1>
            <p className="mt-2 text-sm text-[#6B7280]">Start with the app frontend. Marketing stays on `linirio.com`.</p>

            {error && (
              <div className="mt-6 rounded-2xl border border-[#F2B8B5] bg-[#FFF3F2] px-4 py-3 text-sm text-[#B42318]" data-testid="register-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0B1D3A] mb-2">Full name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="brand-input w-full px-4 py-3"
                  placeholder="Jane Doe"
                  required
                  data-testid="register-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0B1D3A] mb-2">Work email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="brand-input w-full px-4 py-3"
                  placeholder="you@company.com"
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0B1D3A] mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="brand-input w-full px-4 py-3"
                  placeholder="********"
                  required
                  data-testid="register-password-input"
                />
                <ul className="mt-3 grid gap-1.5" data-testid="password-requirements">
                  {checks.map((check) => (
                    <li
                      key={check.key}
                      className={`flex items-center gap-2 text-xs ${check.passed ? 'text-[#376B2B]' : 'text-[#6B7280]'}`}
                      data-testid={`pw-req-${check.key}`}
                      data-passed={check.passed}
                    >
                      {check.passed ? (
                        <Check className="w-3.5 h-3.5 text-[#7BE36A]" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-[#6B7280]" />
                      )}
                      {check.label}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="submit"
                disabled={loading || !allPassed}
                className="w-full py-3.5 bg-[#12C8C6] hover:bg-[#0FB3B1] text-white font-medium rounded-2xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Create account <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <p className="mt-7 text-center text-sm text-[#6B7280]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0B1D3A] font-medium hover:underline">
                Sign in
              </Link>
            </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
